import { getResolution, getUserPosition } from "@breakout/sdk"
import { isPastSec } from "@breakout/shared/utils/time"
import { useEffect, useState } from "react"
import type { Address } from "viem"
import { useBreakoutConfig } from "../../lib/providers"
import { Sentry } from "../../lib/sentry"

export type MarketState = "active" | "ended" | "resolved" | "finalized" | "claimed" | "loading"

interface MarketResolutionState {
	marketState: MarketState
	claimableAmount: bigint
	winningBucket: number | null
}

interface UseMarketResolutionParams {
	marketAddress: Address
	userAddress: Address
	cutoffTimeSec: number | null
}

export function useMarketResolution({
	marketAddress,
	userAddress,
	cutoffTimeSec,
}: UseMarketResolutionParams): MarketResolutionState {
	const config = useBreakoutConfig()

	const [state, setState] = useState<MarketResolutionState>({
		marketState: "loading",
		claimableAmount: 0n,
		winningBucket: null,
	})

	useEffect(() => {
		let cancelled = false

		async function checkResolution() {
			try {
				// Fetch resolution and user position in parallel
				const [resolution, userPosition] = await Promise.all([
					getResolution(config.publicClient, { market: marketAddress }),
					getUserPosition(config.publicClient, { market: marketAddress, user: userAddress }),
				])

				if (cancelled) {
					return
				}

				if (!resolution.isResolved) {
					// Check if market has ended (cutoff passed) but not yet resolved
					const isEnded = cutoffTimeSec !== null && isPastSec(cutoffTimeSec)
					setState({
						marketState: isEnded ? "ended" : "active",
						claimableAmount: 0n,
						winningBucket: null,
					})
					return
				}

				if (userPosition.claimed) {
					setState({
						marketState: "claimed",
						claimableAmount: 0n,
						winningBucket: resolution.winningOutcome,
					})
					return
				}

				const claimable = BigInt(Math.floor(userPosition.claimable * 1e6))
				setState({
					marketState: resolution.isFinalized ? "finalized" : "resolved",
					claimableAmount: claimable,
					winningBucket: resolution.winningOutcome,
				})
			} catch (error) {
				console.error("[useMarketResolution] Failed to check resolution:", {
					error,
					marketAddress,
					userAddress,
				})
				Sentry.captureException(error, {
					tags: { operation: "checkResolution", domain: "market" },
					extra: { marketAddress, userAddress },
				})
				if (!cancelled) {
					setState({
						marketState: "active",
						claimableAmount: 0n,
						winningBucket: null,
					})
				}
			}
		}

		void checkResolution()
		return () => {
			cancelled = true
		}
	}, [config, marketAddress, userAddress, cutoffTimeSec])

	return state
}
